import React, { useState } from 'react';
import { useAddProjectImagesMutation, useAdminLoginMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config/api';

const TestAddProjectImages: React.FC = () => {
  const [addImages, { isLoading }] = useAddProjectImagesMutation();
  const [adminLogin, { isLoading: isLoginLoading }] = useAdminLoginMutation();
  const [projectId, setProjectId] = useState<string>('11');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
  };

  const testLogin = async () => {
    try {
      console.log('Testing admin login...');
      
      // You can modify these credentials as needed
      const credentials = {
        email: 'super.admin@gmail.com', // Change to your admin email
        password: 'password123', // Change to your admin password
        role: 'SU' as 'SU' | 'AU'
      };

      const result = await adminLogin(credentials).unwrap();
      
      if (result.success) {
        console.log('Login successful:', result);
        toast.success('Login successful! Token saved.');
        
        // Save token to localStorage
        localStorage.setItem('diasporawise_admin_token', result.data.token);
        localStorage.setItem('diasporawise_admin_user', JSON.stringify(result.data.user));
        
        return true;
      } else {
        toast.error(`Login failed: ${result.message}`);
        return false;
      }
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(`Login failed: ${error?.data?.message || error?.message || 'Unknown error'}`);
      return false;
    }
  };

  const testToken = async () => {
    try {
      console.log('Testing authentication token...');
      
      const token = localStorage.getItem('diasporawise_admin_token');
      if (!token) {
        toast.error('No authentication token found. Please login first.');
        return false;
      }

      console.log('Token found:', token.substring(0, 20) + '...');
      
      // Test token with a simple API call
      const response = await fetch(`${API_BASE_URL}/admin/dashboard-details`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Token test response status:', response.status);
      
      if (response.status === 401) {
        toast.error('Token is invalid or expired. Please login again.');
        localStorage.removeItem('diasporawise_admin_token');
        localStorage.removeItem('diasporawise_admin_user');
        return false;
      }
      
      if (response.ok) {
        toast.success('Token is valid!');
        return true;
      }
      
      toast.error(`Token test failed: ${response.status}`);
      return false;
    } catch (error) {
      console.error('Token test error:', error);
      toast.error(`Token test failed: ${error}`);
      return false;
    }
  };

  const testDirectFetch = async (projectId: number, files: File[]) => {
    try {
      console.log('Testing direct fetch...');
      
      // First test the token
      const tokenValid = await testToken();
      if (!tokenValid) {
        return;
      }
      
      const formData = new FormData();
      formData.append('project_id', projectId.toString());
      files.forEach(file => {
        formData.append('images', file);
      });

      const token = localStorage.getItem('diasporawise_admin_token');
      
      const response = await fetch(`${API_BASE_URL}/admin/add-project-images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Don't set Content-Type for FormData
        },
        body: formData
      });

      console.log('Direct fetch response status:', response.status);
      console.log('Direct fetch response headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('Direct fetch response text:', responseText);
      
      if (response.status === 401) {
        toast.error('Authentication failed. Token may be expired. Please login again.');
        localStorage.removeItem('diasporawise_admin_token');
        localStorage.removeItem('diasporawise_admin_user');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Direct fetch failed: ${response.status} ${response.statusText}`);
      }
      
      toast.success('Direct fetch test successful!');
    } catch (error) {
      console.error('Direct fetch error:', error);
      toast.error(`Direct fetch failed: ${error}`);
    }
  };

  const handleSubmit = async () => {
    if (!projectId || selectedFiles.length === 0) {
      toast.error('Please select a project ID and at least one image');
      return;
    }

    // Validate project ID
    const projectIdNum = parseInt(projectId);
    if (isNaN(projectIdNum) || projectIdNum <= 0) {
      toast.error('Please enter a valid project ID (positive number)');
      return;
    }

    try {
      console.log('Testing add-project-images API...');
      console.log('Project ID:', projectIdNum);
      console.log('Files:', selectedFiles);
      
      // Validate files before sending
      const validFiles = selectedFiles.filter(file => {
        if (!file.type.startsWith('image/')) {
          toast.error(`File ${file.name} is not a valid image file`);
          return false;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`File ${file.name} is too large (max 10MB)`);
          return false;
        }
        if (file.size < 1024) {
          toast.error(`File ${file.name} is too small (min 1KB)`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) {
        toast.error('No valid image files selected');
        return;
      }

      console.log('Valid files:', validFiles);
      console.log('File details:', validFiles.map(f => ({
        name: f.name,
        type: f.type,
        size: f.size
      })));

      // Test with direct fetch first
      await testDirectFetch(projectIdNum, validFiles);

      const result = await addImages({
        project_id: projectIdNum,
        images: validFiles
      }).unwrap();

      // Safety check (though baseQuery now handles success: false as errors)
      if (result && result.success === false) {
        toast.error(result.message || 'Failed to upload images');
        return;
      }

      console.log('API Response:', result);
      toast.success('Images uploaded successfully!');
    } catch (error: any) {
      console.error('API Error:', error);
      
      // Extract error message from various possible structures
      let errorMessage = 'Unknown error';
      if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.error) {
        errorMessage = error.data.error;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Handle specific error cases
      if (error?.status === 400 || error?.data?.status === 400) {
        toast.error(`Bad Request (400): ${errorMessage}. Please check the image files and project ID.`);
      } else if (error?.status === 401 || error?.data?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 413 || error?.data?.status === 413) {
        toast.error('File too large. Please reduce image size.');
      } else if (error?.status === 415 || error?.data?.status === 415) {
        toast.error('Unsupported file type. Please use JPG, PNG, or GIF images.');
      } else {
        toast.error(`Upload failed: ${errorMessage}`);
      }
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-md mx-auto">
      <h2 className="text-xl font-bold mb-4">Test Add Project Images API</h2>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Project ID
          </label>
          <input
            type="number"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter project ID"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Images
          </label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {selectedFiles.length > 0 && (
            <p className="text-sm text-gray-600 mt-1">
              {selectedFiles.length} file(s) selected
            </p>
          )}
        </div>

        <div className="space-y-2">
          <button
            onClick={testLogin}
            disabled={isLoginLoading}
            className="w-full bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
          >
            {isLoginLoading ? 'Logging in...' : 'Test Admin Login'}
          </button>
          
          <button
            onClick={testToken}
            className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            Test Authentication Token
          </button>
          
          <button
            onClick={handleSubmit}
            disabled={isLoading || !projectId || selectedFiles.length === 0}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Uploading...' : 'Test Upload Images (RTK Query + Direct Fetch)'}
          </button>
          
          <button
            onClick={() => {
              if (!projectId || selectedFiles.length === 0) {
                toast.error('Please select a project ID and at least one image');
                return;
              }
              const projectIdNum = parseInt(projectId);
              if (isNaN(projectIdNum) || projectIdNum <= 0) {
                toast.error('Please enter a valid project ID (positive number)');
                return;
              }
              testDirectFetch(projectIdNum, selectedFiles);
            }}
            disabled={!projectId || selectedFiles.length === 0}
            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Test Direct Fetch Only
          </button>
        </div>
      </div>

      <div className="mt-4 p-3 bg-gray-100 rounded-md">
        <h3 className="font-medium text-gray-700 mb-2">API Endpoint:</h3>
        <code className="text-sm text-gray-600">
          POST /admin/add-project-images
        </code>
        <br />
        <h3 className="font-medium text-gray-700 mb-2 mt-2">Expected Response:</h3>
        <code className="text-sm text-gray-600">
          {`{
  "success": true,
  "message": "Images uploaded successfully"
}`}
        </code>
      </div>
    </div>
  );
};

export default TestAddProjectImages;
